import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ShoppingCart,
  Inventory,
  CalendarToday,
  MoreHoriz,
  Close,
  People,
  Receipt,
  Payment,
  AttachMoney,
  Settings,
  PersonAdd,
  Description,
  ContactMail,
  EventNote,
  Assessment,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const MobileNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [moreDrawerOpen, setMoreDrawerOpen] = useState(false);

  // Primary navigation items shown in bottom nav
  const primaryNavItems = [
    { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { label: 'Sales', icon: <ShoppingCart />, path: '/sales' },
    { label: 'Inventory', icon: <Inventory />, path: '/inventory' },
    { label: 'Calendar', icon: <CalendarToday />, path: '/calendar' },
  ];

  // Secondary items shown in "More" drawer
  const secondaryNavItems = [
    { text: 'Appointments', icon: <EventNote />, path: '/appointments' },
    { text: 'Staff Schedule', icon: <People />, path: '/staff-schedule' },
    { text: 'Leads', icon: <People />, path: '/leads' },
    { text: 'Clients', icon: <People />, path: '/clients' },
    { text: 'Invoices', icon: <Receipt />, path: '/invoices' },
    { text: 'Payments', icon: <Payment />, path: '/payments' },
    { text: 'Commissions', icon: <AttachMoney />, path: '/commissions' },
    { text: 'Documents', icon: <Description />, path: '/documents' },
  ];

  const adminNavItems = [
    { text: 'Contact Requests', icon: <ContactMail />, path: '/contact-requests' },
    { text: 'Reports', icon: <Assessment />, path: '/reports' },
    { text: 'Settings', icon: <Settings />, path: '/settings' },
    { text: 'Users', icon: <PersonAdd />, path: '/users' },
  ];

  // Determine current active tab
  const getCurrentTab = () => {
    const currentPath = location.pathname;
    const primaryIndex = primaryNavItems.findIndex(item => item.path === currentPath);
    if (primaryIndex !== -1) return primaryIndex;

    // If on a secondary page, show "More" as active
    const isSecondary = secondaryNavItems.some(item => item.path === currentPath) ||
                        (isAdmin && adminNavItems.some(item => item.path === currentPath));
    if (isSecondary) return 4; // "More" index

    return 0; // Default to Dashboard
  };

  const handleNavigation = (event, newValue) => {
    if (newValue === 4) {
      // "More" button clicked
      setMoreDrawerOpen(true);
    } else {
      navigate(primaryNavItems[newValue].path);
    }
  };

  const handleSecondaryNavigation = (path) => {
    navigate(path);
    setMoreDrawerOpen(false);
  };

  return (
    <>
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          display: { xs: 'block', md: 'none' },
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        }}
        elevation={3}
      >
        <BottomNavigation
          value={getCurrentTab()}
          onChange={handleNavigation}
          showLabels
          sx={{
            height: 64,
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              padding: '6px 0',
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
            },
          }}
        >
          {primaryNavItems.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={item.label}
              icon={item.icon}
            />
          ))}
          <BottomNavigationAction
            label="More"
            icon={<MoreHoriz />}
          />
        </BottomNavigation>
      </Paper>

      {/* More Drawer */}
      <Drawer
        anchor="bottom"
        open={moreDrawerOpen}
        onClose={() => setMoreDrawerOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            maxHeight: '80vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        }}
      >
        <Box sx={{ pb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              pb: 1,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              More Options
            </Typography>
            <IconButton onClick={() => setMoreDrawerOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
          <Divider />

          <List>
            {secondaryNavItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => handleSecondaryNavigation(item.path)}
                  sx={{
                    py: 1.5,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(0, 51, 102, 0.08)',
                      borderLeft: '4px solid',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}

            {isAdmin && (
              <>
                <Divider sx={{ my: 1 }} />
                <ListItem>
                  <ListItemText
                    primary="ADMINISTRATION"
                    primaryTypographyProps={{
                      variant: 'caption',
                      color: 'text.secondary',
                      fontWeight: 600,
                      px: 2,
                    }}
                  />
                </ListItem>
                {adminNavItems.map((item) => (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton
                      selected={location.pathname === item.path}
                      onClick={() => handleSecondaryNavigation(item.path)}
                      sx={{
                        py: 1.5,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(0, 51, 102, 0.08)',
                          borderLeft: '4px solid',
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default MobileNav;
